import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
// IMPORTANT:
// `isomorphic-dompurify` pulls `jsdom`, and recent jsdom versions depend on packages
// that require newer Node runtimes / ESM-only transitive deps which can break in Vercel
// serverless bundles ("ERR_REQUIRE_ESM").
// We only need sanitization for *browser rendering* (`dangerouslySetInnerHTML`),
// so we keep sanitization on the client and return unsanitized HTML on the server.
import DOMPurify from "dompurify";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const stripStrayBackticksInHtml = (content: string): string => {
  return (
    content
      // Remove &nbsp; (and repeated &nbsp;) from empty table cells: <td>&nbsp;</td>
      .replace(/<(td|th)([^>]*)>\s*(?:&nbsp;)+\s*<\/(td|th)>/gi, "<$1$2></$3>")
      // Remove lone backtick from empty table cells: <td>`</td>
      .replace(/<(td|th)([^>]*)>\s*`\s*<\/(td|th)>/gi, "<$1$2></$3>")
      // Removes CMS artifacts like "a`practice" or "the`personal" while preserving normal apostrophes.
      .replace(/([A-Za-z0-9])`(?=[A-Za-z0-9])/g, "$1")
  );
};

export const sanitizeHTML = (content: string) => {
  const normalizedContent = stripStrayBackticksInHtml(content || "");

  // On the server, do not attempt to sanitize (would require a DOM implementation).
  // The content is still rendered via React's `dangerouslySetInnerHTML` on the client.
  if (typeof window === "undefined") {
    return { __html: normalizedContent };
  }

  return {
    __html: DOMPurify.sanitize(normalizedContent, {
      ADD_ATTR: ["target"],
      ADD_TAGS: ["iframe"],
    }),
  };
};

export const shimmer = (w: number, h: number) => `
  <svg width="${w}" height="${h}" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
    <defs>
      <linearGradient id="g">
        <stop stop-color="#333" offset="20%" />
        <stop stop-color="#222" offset="50%" />
        <stop stop-color="#333" offset="70%" />
      </linearGradient>
    </defs>
    <rect width="${w}" height="${h}" fill="#333" />
    <rect id="r" width="${w}" height="${h}" fill="url(#g)" />
  </svg>`

export const toBase64 = (str: string) =>
  typeof window === 'undefined'
    ? Buffer.from(str).toString('base64')
    : window.btoa(str)

// Maximum concurrent requests to avoid overwhelming Vercel/Directus
export const MAX_CONCURRENT_REQUESTS = 3;

/**
 * Execute promises with a concurrency limit
 * @param items - Array of items to process
 * @param fn - Async function to execute for each item
 * @param limit - Maximum concurrent executions (default: MAX_CONCURRENT_REQUESTS)
 */
export async function promiseAllWithLimit<T, R>(
  items: T[],
  fn: (item: T) => Promise<R>,
  limit: number = MAX_CONCURRENT_REQUESTS
): Promise<R[]> {
  const results: R[] = [];
  const executing: Promise<void>[] = [];

  for (const item of items) {
    const promise = fn(item).then((result) => {
      results.push(result);
    });

    executing.push(promise);

    if (executing.length >= limit) {
      await Promise.race(executing);
      // Remove completed promises
      const completed = executing.filter((p) => {
        let isResolved = false;
        p.then(() => (isResolved = true)).catch(() => (isResolved = true));
        return !isResolved;
      });
      executing.length = 0;
      executing.push(...completed);
    }
  }

  await Promise.all(executing);
  return results;
}

/**
 * Execute an array of promise-returning functions with a concurrency limit
 * @param fns - Array of functions that return promises
 * @param limit - Maximum concurrent executions
 */
export async function promisePoolAll<T>(
  fns: (() => Promise<T>)[],
  limit: number = MAX_CONCURRENT_REQUESTS
): Promise<T[]> {
  const results: T[] = new Array(fns.length);
  let currentIndex = 0;

  async function worker() {
    while (currentIndex < fns.length) {
      const index = currentIndex++;
      results[index] = await fns[index]();
    }
  }

  const workers = Array(Math.min(limit, fns.length))
    .fill(null)
    .map(() => worker());

  await Promise.all(workers);
  return results;
}
